import { BaseComponentImpl } from '../../../../types/implementations';
import { ComponentLifetime, MaintenanceTemplate } from '../../TemplatesEditor/types';
import { getChartColor } from '../../../../Helpers/chartColors';
import { resolveComponentSchedule } from '../../../../Helpers/maintenanceSchedule';
import { ComponentMaintenanceDataWithDone } from '../types';
import { calculateInstallDate } from './calculations';
import { Building } from '../../../../types/protocols';

export interface TimelineActivity {
    year: number;
    type: 'replacement' | 'maintenance';
    name: string;
    templateId?: string;
    color?: string;
    count?: number; // Number of occurrences in this year
}

/**
 * Generate timeline activities for a component
 * This includes replacement and maintenance activities over the component's lifetime
 * Replacement cycles and maintenance are extrapolated through endYear (inclusive).
 */
export const generateTimelineActivities = (
    component: BaseComponentImpl,
    lifetime: ComponentLifetime,
    templates: MaintenanceTemplate[],
    draftComponents: { [componentKey: string]: ComponentMaintenanceDataWithDone },
    building: Building | null,
    endYear: number
): TimelineActivity[] => {
    const installDate = calculateInstallDate(component, lifetime, building);
    if (!installDate || !building) return [];

    const activities: TimelineActivity[] = [];

    const componentKey = component.key;
    const currentYear = new Date().getFullYear();

    // Replacement and maintenance come from the shared resolver, so the card
    // shows exactly what the preview sums and activation snapshots. The draft
    // entry (or the condition, when it has none) decides the first replacement.
    const schedule = resolveComponentSchedule({
        component,
        entry: draftComponents[componentKey] ?? { maintenanceActivities: {} },
        building,
        lifetime,
        typeTemplates: templates,
        findTemplate: id => templates.find(t => t.id === id),
        window: { startYear: currentYear, endYear: endYear + 1 },
        isDraft: true
    });
    if (!schedule) return [];

    schedule.replacement?.years.forEach((year, cycleIndex) => {
        activities.push({
            year,
            type: 'replacement',
            name: 'Livslängd',
            templateId: `${componentKey}-replacement${cycleIndex > 0 ? `-cycle-${cycleIndex}` : ''}`,
            count: 1
        });
    });

    schedule.activities.forEach(activity => {
        // Colour follows the template's position in the card's list, so it stays
        // put when another activity is disabled
        const templateIndex = Math.max(0, templates.findIndex(t => t.id === activity.templateId));
        const color = getChartColor(templateIndex);

        const occurrencesByYear = new Map<number, number>();
        activity.dates.forEach(date => {
            const year = Number(date.slice(0, 4));
            occurrencesByYear.set(year, (occurrencesByYear.get(year) || 0) + 1);
        });

        occurrencesByYear.forEach((count, year) => {
            activities.push({
                year,
                type: 'maintenance',
                name: activity.template.name || 'Underhåll',
                templateId: `${componentKey}-${activity.templateId}`,
                color,
                count
            });
        });
    });

    return activities.sort((a, b) => a.year - b.year);
};
